import React from 'react'
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { IconSeeker, FlagsIconSeeker } from '@ui-seeker/core'
import { editMappingDrawer, selectedUserForAnalysis, userAnalysisOpen } from '../@signals'

// Format relative time (input is UTC from database)
const formatRelativeTime = (dateString) => {
    if (!dateString) return '-'

    // Parse as UTC - append 'Z' if not present to force UTC interpretation
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z'
    const date = new Date(utcString)
    const now = new Date()
    const diffMs = now - date

    // Handle negative diff (future dates)
    if (diffMs < 0) return 'Just now'

    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    // More precise formatting
    if (diffSecs < 60) return `${diffSecs}s ago`
    if (diffMins < 60) {
        const remainingSecs = diffSecs % 60
        return remainingSecs > 0 ? `${diffMins}m ${remainingSecs}s ago` : `${diffMins}m ago`
    }
    if (diffHours < 24) {
        const remainingMins = diffMins % 60
        return remainingMins > 0 ? `${diffHours}h ${remainingMins}m ago` : `${diffHours}h ago`
    }
    if (diffDays < 7) {
        const remainingHours = diffHours % 24
        return remainingHours > 0 ? `${diffDays}d ${remainingHours}h ago` : `${diffDays}d ago`
    }
    return date.toLocaleDateString()
}

// Convert UTC date string to local Date object
const parseUTCDate = (dateString) => {
    if (!dateString) return null
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z'
    return new Date(utcString)
}

// Action chip colors
const actionColors = {
    'MAP': 'success',
    'UNMAP': 'error',
    'REMAP': 'warning'
}

// Common centered cell styles
const centeredCellProps = {
    setCellProps: () => ({ style: { textAlign: 'center', verticalAlign: 'middle' } }),
    setCellHeaderProps: () => ({ style: { textAlign: 'center' } })
}

export const timestampColumn = () => ({
    name: 'created_at',
    label: 'Time',
    options: {
        filter: false,
        sort: true,
        ...centeredCellProps,
        customBodyRender: (value) => {
            const localDate = parseUTCDate(value)
            return (
                <Tooltip title={localDate ? localDate.toLocaleString() : '-'}>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(value)}
                    </Typography>
                </Tooltip>
            )
        }
    }
})

export const actionColumn = () => ({
    name: 'action',
    label: 'Action',
    options: {
        filter: true,
        sort: true,
        ...centeredCellProps,
        customBodyRender: (value, tableMeta) => {
            const rowData = tableMeta.rowData
            // propagatedSiteNameColumn is at index 17, propagatedFromSiteColumn is at index 18
            const propagatedSiteName = rowData[17]
            const propagatedFromSite = rowData[18]

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Chip
                        label={value}
                        size="small"
                        color={actionColors[value] || 'default'}
                        sx={{ fontWeight: 'bold', minWidth: 70 }}
                    />
                    {propagatedFromSite && (
                        <Tooltip title={`Propagated from ${propagatedSiteName || `Site ${propagatedFromSite}`}`}>
                            <Box sx={{ display: 'flex', color: 'info.main' }}>
                                <IconSeeker icon="share-from-square" size="xs" />
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            )
        }
    }
})

export const productColumn = () => ({
    name: 'product_id',
    label: 'Product',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
            const rowData = tableMeta.rowData
            // siteColumn is at index 3, productInfoColumn is at index 7
            // siteNameColumn is at index 5
            // idSkuNewColumn is at index 9, skuNewInfoColumn is at index 11
            // idSkuOldColumn is at index 8, skuOldInfoColumn is at index 10
            const idSite = rowData[3]
            const siteName = rowData[5]
            const productInfo = rowData[7]
            const idSkuOld = rowData[8]
            const idSkuNew = rowData[9]
            const skuOldInfo = rowData[10]
            const skuNewInfo = rowData[11]
            // raw2raw context (indexes per the columns array in Table/index.jsx)
            const mappingType = rowData[20]
            const idMatch = rowData[21]
            const matchInfo = rowData[22]
            const idCustomer = rowData[23]

            // Get id_brand from the available SKU info (new first, then old)
            const idBrand = skuNewInfo?.id_brand || skuOldInfo?.id_brand

            // Determine current SKU (new for MAP/REMAP, null for UNMAP)
            const currentSku = idSkuNew && idSkuNew !== 0
                ? { id_sku: idSkuNew, name: skuNewInfo?.name, image: skuNewInfo?.image_url }
                : null

            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => {
                        editMappingDrawer.value = {
                            raw: {
                                product_id: value,
                                id_site: idSite,
                                title: productInfo?.product_title,
                                subtitle: productInfo?.subtitle,
                                image: productInfo?.thumbnail,
                                variant_raw: productInfo?.variant_raw,
                                product_url: productInfo?.product_url,
                                brand: productInfo?.brand,
                                id_brand: idBrand,
                                site: siteName
                            },
                            sku: currentSku,
                            // raw2raw editing (group view + unmap + remap)
                            mappingType,
                            idMatch,
                            matchInfo,
                            idCustomer
                        }
                    }}
                >
                    {productInfo?.thumbnail && (
                        <img
                            src={productInfo.thumbnail}
                            alt=""
                            style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
                            onError={(e) => { e.target.style.display = 'none' }}
                        />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Tooltip title={productInfo?.product_title || `Product ${value}`}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: 'medium',
                                    maxWidth: 180,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {productInfo?.product_title || `Product ${value}`}
                            </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                            ID: {value}
                        </Typography>
                        <VariantTag value={productInfo?.variant_raw} />
                    </Box>
                </Box>
            )
        }
    }
})

export const siteColumn = () => ({
    name: 'id_site',
    label: 'Site',
    options: {
        filter: true,
        sort: true,
        display: false
    }
})

export const siteCountryCodeColumn = () => ({
    name: 'site_country_code',
    options: { display: false, filter: false }
})

export const siteNameColumn = () => ({
    name: 'site_name',
    label: 'Site',
    options: {
        filter: true,
        sort: true,
        ...centeredCellProps,
        customBodyRender: (value, tableMeta) => {
            const rowData = tableMeta.rowData
            // siteColumn is at index 3, siteCountryCodeColumn is at index 4
            const idSite = rowData[3]
            const countryCode = rowData[4]
            return (
                <Tooltip title={`ID: ${idSite}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                        {countryCode && <FlagsIconSeeker countryName={countryCode} />}
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            {value || `Site ${idSite}`}
                        </Typography>
                    </Box>
                </Tooltip>
            )
        }
    }
})

// Highlighted variant tag — the variant is validation-critical (size/shade traps),
// so it gets emphasis instead of a faint caption
const VariantTag = ({ value }) => value ? (
    <Typography
        variant="caption"
        sx={{
            display: 'inline-block',
            mt: 0.25,
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: 'action.selected',
            color: 'text.primary',
            fontWeight: 600,
            lineHeight: 1.5,
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }}
    >
        {value}
    </Typography>
) : null

// Helper component for SKU display with image
const SkuDisplay = ({ skuInfo, skuId, color = 'text.primary' }) => {
    if (!skuId) return <Typography variant="body2" color="text.secondary">-</Typography>

    // Build subtitle with size and color
    const subtitle = [skuInfo?.size, skuInfo?.color].filter(Boolean).join(' - ')

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {skuInfo?.image_url && (
                <img
                    src={skuInfo.image_url}
                    alt=""
                    style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none' }}
                />
            )}
            <Box sx={{ minWidth: 0 }}>
                <Tooltip title={skuInfo?.name || `SKU ${skuId}`}>
                    <Typography variant="body2" color={color} fontWeight="medium" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {skuInfo?.name || skuId}
                    </Typography>
                </Tooltip>
                {subtitle && (
                    <Box><VariantTag value={subtitle} /></Box>
                )}
            </Box>
        </Box>
    )
}

export const skuColumn = () => ({
    name: 'sku_change',
    label: 'SKU / Match',
    options: {
        filter: false,
        sort: false,
        customBodyRender: (_, tableMeta) => {
            const rowData = tableMeta.rowData
            // Column indices after reordering:
            // idSkuOldColumn: 8, idSkuNewColumn: 9, skuOldInfoColumn: 10, skuNewInfoColumn: 11
            // mappingTypeColumn: 20, idMatchColumn: 21
            const oldSku = rowData[8]
            const newSku = rowData[9]
            const oldSkuInfo = rowData[10]
            const newSkuInfo = rowData[11]
            const mappingType = rowData[20]
            const idMatch = rowData[21]

            // raw2raw: show match group with origin product info
            if (mappingType === 'raw2raw') {
                const matchInfo = rowData[22] // match_info
                const originName = matchInfo?.origin_name
                const matchImage = matchInfo?.image_url

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {matchImage && (
                            <img
                                src={matchImage}
                                alt=""
                                style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
                                onError={(e) => { e.target.style.display = 'none' }}
                            />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Tooltip title={originName || `Match #${idMatch}`}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#7c3aed',
                                        fontWeight: 'medium',
                                        maxWidth: 180,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {originName || `Match #${idMatch}`}
                                </Typography>
                            </Tooltip>
                            <VariantTag value={matchInfo?.variant_raw} />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                Match #{idMatch}
                            </Typography>
                        </Box>
                    </Box>
                )
            }

            if (!oldSku && newSku) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">-</Typography>
                        <IconSeeker icon="arrow-right" size="xs" />
                        <SkuDisplay skuInfo={newSkuInfo} skuId={newSku} color="success.main" />
                    </Box>
                )
            }

            if (oldSku && newSku === 0) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ textDecoration: 'line-through', opacity: 0.6 }}>
                            <SkuDisplay skuInfo={oldSkuInfo} skuId={oldSku} color="text.secondary" />
                        </Box>
                        <IconSeeker icon="arrow-right" size="xs" />
                        <Typography variant="body2" color="error.main">
                            (unmapped)
                        </Typography>
                    </Box>
                )
            }

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ opacity: 0.6 }}>
                        <SkuDisplay skuInfo={oldSkuInfo} skuId={oldSku} color="text.secondary" />
                    </Box>
                    <IconSeeker icon="arrow-right" size="xs" />
                    <SkuDisplay skuInfo={newSkuInfo} skuId={newSku} color="warning.main" />
                </Box>
            )
        }
    }
})

// Curated source styles (distinct colors for the common ones). Anything not listed gets
// readable initials + a deterministic color derived from the source string (see getSourceStyle),
// so no source ever renders as a meaningless "?".
const sourceConfig = {
    'RAW_MAPPING': { color: '#6366f1', bg: '#eef2ff', initials: 'RM', label: 'Raw mapping' },
    'SKU_MATCHER': { color: '#8b5cf6', bg: '#f5f3ff', initials: 'SM', label: 'SKU matcher' },
    'UNMATCHED': { color: '#ef4444', bg: '#fef2f2', initials: 'UN', label: 'Unmatched' },
    'REVERSE_MAPPING': { color: '#f59e0b', bg: '#fffbeb', initials: 'RV', label: 'Reverse mapping' },
    'product-review': { color: '#10b981', bg: '#ecfdf5', initials: 'PR', label: 'Product review' },
    'product-review-multiple': { color: '#14b8a6', bg: '#f0fdfa', initials: 'PM', label: 'Product review (multiple)' },
    'product-review-remove-multiple': { color: '#f43f5e', bg: '#fff1f2', initials: 'RX', label: 'Product review (remove multiple)' },
    'product-review-group-map-missing': { color: '#059669', bg: '#ecfdf5', initials: 'GM', label: 'Product review (group map missing)' },
    'sku-management': { color: '#0ea5e9', bg: '#f0f9ff', initials: 'SK', label: 'SKU management' },
    'batch': { color: '#64748b', bg: '#f8fafc', initials: 'BT', label: 'Batch' },
    'EAN_MATCH': { color: '#22c55e', bg: '#f0fdf4', initials: 'EA', label: 'EAN match' },
    'IA_MATCH': { color: '#a855f7', bg: '#faf5ff', initials: 'IA', label: 'IA match' },
    'ean-match-batch': { color: '#22c55e', bg: '#f0fdf4', initials: 'EB', label: 'EAN match (batch)' },
    'retailer-products-dashboard': { color: '#06b6d4', bg: '#ecfeff', initials: 'RD', label: 'Retailer products dashboard' },
    'reference-mapping-script': { color: '#78716c', bg: '#fafaf9', initials: 'RF', label: 'Reference mapping script' },
    'sku-mapping-migration': { color: '#94a3b8', bg: '#f8fafc', initials: 'MG', label: 'SKU mapping migration' },
    'catalog-details': { color: '#0891b2', bg: '#ecfeff', initials: 'CD', label: 'Catalog details' },
    'product_mapping_svc': { color: '#6366f1', bg: '#eef2ff', initials: 'PS', label: 'Product mapping service (raw→SKU)' },
    'sku_mapping_svc': { color: '#8b5cf6', bg: '#f5f3ff', initials: 'KS', label: 'SKU mapping service (SKU→raw)' },
    'product_batch_autovalidate': { color: '#0ea5e9', bg: '#f0f9ff', initials: 'AV', label: 'Batch auto-validate' },
    'retailer-unmap-synergy': { color: '#ef4444', bg: '#fef2f2', initials: 'US', label: 'Retailer unmap (synergy)' },
    'raw2raw-ia-auto': { color: '#a855f7', bg: '#faf5ff', initials: 'R2', label: 'raw2raw IA auto-validate' },
    'products-matcher': { color: '#14b8a6', bg: '#f0fdfa', initials: 'PX', label: 'Products matcher (raw2raw)' },
    'audit-log': { color: '#64748b', bg: '#f8fafc', initials: 'AL', label: 'Audit log edit' }
}

// Pastel bg + strong fg pairs for sources without a curated entry.
const FALLBACK_PALETTE = [
    { color: '#6366f1', bg: '#eef2ff' }, { color: '#8b5cf6', bg: '#f5f3ff' },
    { color: '#0ea5e9', bg: '#f0f9ff' }, { color: '#10b981', bg: '#ecfdf5' },
    { color: '#f59e0b', bg: '#fffbeb' }, { color: '#ef4444', bg: '#fef2f2' },
    { color: '#06b6d4', bg: '#ecfeff' }, { color: '#a855f7', bg: '#faf5ff' },
    { color: '#14b8a6', bg: '#f0fdfa' }, { color: '#78716c', bg: '#fafaf9' }
]

const deriveInitials = (str) => {
    const words = String(str).split(/[-_\s]+/).filter(Boolean)
    if (!words.length) return '?'
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
    return (words[0][0] + words[1][0]).toUpperCase()
}

const hashPalette = (str) => {
    let h = 0
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0
    return FALLBACK_PALETTE[Math.abs(h) % FALLBACK_PALETTE.length]
}

// Resolve the visual style + human label for any source value, handling the "_propagated"
// suffix (same look as its base source, flagged as propagated).
const getSourceStyle = (value) => {
    if (!value) return { color: '#9ca3af', bg: '#f9fafb', initials: '?', label: 'Unknown', propagated: false }
    const propagated = /_propagated$/.test(value)
    const base = value.replace(/_propagated$/, '')
    const cfg = sourceConfig[value] || sourceConfig[base]
    if (cfg) return { ...cfg, propagated }
    const { color, bg } = hashPalette(base)
    return { color, bg, initials: deriveInitials(base), label: base, propagated }
}

export const sourceColumn = () => ({
    name: 'source',
    label: 'Src',
    options: {
        filter: true,
        sort: true,
        ...centeredCellProps,
        customBodyRender: (value) => {
            const config = getSourceStyle(value)
            const tooltip = config.propagated ? `${config.label} (propagated) · ${value}` : `${config.label} · ${value}`
            return (
                <Tooltip title={tooltip}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: config.bg,
                            color: config.color,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            // propagated mappings get a subtle ring to set them apart from the original action
                            border: config.propagated ? `1.5px dashed ${config.color}` : 'none'
                        }}
                    >
                        {config.initials}
                    </Box>
                </Tooltip>
            )
        }
    }
})

export const userColumn = () => ({
    name: 'user_email',
    label: 'User',
    options: {
        filter: true,
        sort: true,
        ...centeredCellProps,
        customBodyRender: (value, tableMeta) => {
            const rowData = tableMeta.rowData
            // userIdColumn is at index 14
            const userId = rowData[14]
            if (!value && !userId) return <Typography variant="body2" color="text.secondary">System</Typography>

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => {
                            selectedUserForAnalysis.value = userId
                            userAnalysisOpen.value = true
                        }}
                    >
                        {value || `User ${userId}`}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => {
                            selectedUserForAnalysis.value = userId
                            userAnalysisOpen.value = true
                        }}
                    >
                        <IconSeeker icon="chart-line" size="xs" />
                    </IconButton>
                </Box>
            )
        }
    }
})

export const confidenceColumn = () => ({
    name: 'ia_distance',
    label: 'IA',
    options: {
        filter: false,
        sort: true,
        ...centeredCellProps,
        customBodyRender: (value) => {
            if (value === null || value === undefined) return '-'
            const percent = Math.round(value * 100)

            // Heat color: red (0%) -> yellow (50%) -> green (100%)
            const getHeatColor = (pct) => {
                if (pct >= 90) return { bg: '#dcfce7', text: '#166534' } // green
                if (pct >= 80) return { bg: '#fef9c3', text: '#854d0e' } // yellow
                if (pct >= 70) return { bg: '#ffedd5', text: '#9a3412' } // orange
                return { bg: '#fee2e2', text: '#991b1b' } // red
            }

            const colors = getHeatColor(percent)

            return (
                <Tooltip title={`IA Confidence: ${percent}%`}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: colors.bg,
                            color: colors.text,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            minWidth: 40
                        }}
                    >
                        {percent}%
                    </Box>
                </Tooltip>
            )
        }
    }
})

export const propagatedFromSiteColumn = () => ({
    name: 'propagated_from_site',
    options: { display: false, filter: false }
})

// Hidden columns for data access
export const idAuditColumn = () => ({
    name: 'id_audit',
    options: { display: false, filter: false }
})

export const idSkuOldColumn = () => ({
    name: 'id_sku_old',
    options: { display: false, filter: false }
})

export const idSkuNewColumn = () => ({
    name: 'id_sku_new',
    options: { display: false, filter: false }
})

export const userIdColumn = () => ({
    name: 'user_id',
    options: { display: false, filter: false }
})

export const propagatedSiteNameColumn = () => ({
    name: 'propagated_site_name',
    options: { display: false, filter: false }
})

export const iaTextColumn = () => ({
    name: 'ia_text',
    label: 'IA Method',
    options: {
        display: false,
        filter: false,
        sort: false
    }
})

// Hidden columns for enriched data
export const skuOldInfoColumn = () => ({
    name: 'sku_old_info',
    options: { display: false, filter: false }
})

export const skuNewInfoColumn = () => ({
    name: 'sku_new_info',
    options: { display: false, filter: false }
})

export const productInfoColumn = () => ({
    name: 'product_info',
    options: { display: false, filter: false }
})

const mappingTypeConfig = {
    'raw2raw': { label: 'R2R', color: '#6d28d9', bg: '#ede9fe', full: 'Product ↔ Product' },
    'raw2sku': { label: 'R2S', color: '#1d4ed8', bg: '#dbeafe', full: 'Product → SKU' },
    'sku2raw': { label: 'S2R', color: '#0f766e', bg: '#ccfbf1', full: 'SKU → Product' },
    'war2war': { label: 'W2W', color: '#b45309', bg: '#fef3c7', full: 'Competitor → Own' }
}

export const mappingTypeColumn = () => ({
    name: 'mapping_type',
    label: 'Type',
    options: {
        display: true,
        filter: true,
        sort: true,
        ...centeredCellProps,
        customBodyRender: (value) => {
            const cfg = mappingTypeConfig[value]
            if (!cfg) {
                return <Typography variant="caption" color="text.disabled">{value || '—'}</Typography>
            }
            return (
                <Tooltip title={cfg.full}>
                    <Chip label={cfg.label} size="small"
                          sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                </Tooltip>
            )
        }
    }
})

export const idMatchColumn = () => ({
    name: 'id_match',
    options: { display: false, filter: false }
})

export const matchInfoColumn = () => ({
    name: 'match_info',
    options: { display: false, filter: false }
})

export const idCustomerColumn = () => ({
    name: 'id_customer',
    options: { display: false, filter: false }
})
